
import SummaryPrintHeader from '@/components/SummaryPrintHeader.vue';
import { Option } from '@/models/option';
import { OptionGroup } from '@/models/option-group';
import { PackagePathSegment } from '@/models/package-path-segment';
import { PathSegment } from '@/models/path-segment';
import PriceUtil from '@/util/price-util';
import he from 'he';
import { computed, ComputedRef, defineComponent, PropType } from 'vue';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'SummaryPrintView',
  components: {SummaryPrintHeader},
  props: {
    calculatedPriceForDisplay: {
      type: String,
      required: true,
      default: ''
    },
    calculatedExternalPricePartForDisplay: {
      type: String,
      required: true,
      default: ''
    },
    calculatedInternalPricePartForDisplay: {
      type: String,
      required: true,
      default: ''
    },
    chosenAdditionalOptions: {
      type: [] as PropType<Option[]>,
      required: true,
      default: null
    },
    chosenSegmentsTitles: {
      type: [] as PropType<PathSegment[]>,
      required: false,
      default: null
    }
  },
  setup() {
    const store = useStore();
    /**
     * Returns the current date and time formatted as a locale string
     */
    const currentLocalDate: ComputedRef<string> = computed(() => {
      return new Date(Date.now()).toLocaleString();
    });

    /**
     * Returns the currently configured footer text.
     */
    const footerText: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings?.footerText ?? '';
    });

    /**
     * Returns the mortician name.
     */
    const morticianName: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings?.name ?? '';
    });

    /**
     * Returns the main phone number of the mortician
     */
    const morticianPhoneNumber: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings?.phoneNumber ?? '';
    });

    /**
     * Returns the main email address of the mortician
     */
    const morticianEmail: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings?.email ?? '';
    });

    /**
     * Returns the group title associated with the given option or an empty
     * string if none was found.
     * @param option The option to get the group title for.
     */
    function getGroupTitleForOption(option: Option): string {
      return store.state.optionGroups.find((og: OptionGroup) => og.options.includes(option.id))?.title ?? '';
    }

    /**
     * Decodes any entities in a string into regular HTML.
     * @param input The string containing the entities
     */
    function decodeHtmlString(input: string): string {
      return he.decode(input);
    }

    function getOptionPriceForDisplay(price: number): string {
      return '(' + PriceUtil.formatGermanWithFractionsOptional(price) + ')';
    }

    function getSegmentPriceForDisplay(segment: PathSegment): string {
      const price = segment instanceof PackagePathSegment
          ? segment.price || segment.options.reduce((
          pv,
          cv) => pv + cv.price, 0)
          : segment.price;
      return '(' + PriceUtil.formatGermanWithFractionsOptional(price) + ')';
    }

    return {
      currentLocalDate,
      footerText,
      morticianName,
      morticianPhoneNumber,
      morticianEmail,
      getGroupTitleForOption,
      decodeHtmlString,
      getOptionPriceForDisplay,
      getSegmentPriceForDisplay
    };
  }
});
